/**
 * @todo: move the token storage to http only cookies
 * 
 * important: we don't handle the api response errors here, the vue components and the ui logic is in charge of the errors from the api responses (show an alert, vue router redirect or any other meaningful UI interaction base on the error type).
 * the only error we handle here is the 401 unauthorized to refresh the token automatically since it's stricly related to the authentication mechanism.
 */

import axios, { AxiosError } from "axios";
import { AuthResponseDto, Membership, UpdateMembershipDto, User } from "@/generated-sources/anagrafica-api";
import { PadelClub } from "@/models/club.models";
import { ManagerUser, MembershipFilters, MembershipListResponse, PaymentsResponse, UserByClub } from "./types";
import { PaymentFilters } from "./types";

type EventMap = {
    'refreshTokenUpdate': (tokens: AuthResponseDto) => void;
    'refreshTokenFail': () => void;
};

export const ApiClient = class {
    // To implement new API endpoints, follow these guidelines to ensure type-safety and clarity:
    // 1. **Determine API Version and Authentication:** Identify if the endpoint is a V1 or V2 API.
    //    - V1 endpoints typically use `idToken` for authorization via `this.microservicesV1`.
    //    - V2 endpoints typically use `accessToken` for authorization via `this.microservicesV2`.
    // 2. **Define Types:** Create TypeScript interfaces or types for:
    //    - **Request Parameters/Body:** If the endpoint accepts data (e.g., POST, PUT), define an interface for the payload.
    //    - **Response Data:** Define an interface for the expected structure of the API response. These types should ideally reside in `src/api/types.ts` or relevant generated sources.
    // 3. **Create a New Method:** Add a new asynchronous method to the `ApiClient` class.
    // 4. **Make the API Call:** Use the appropriate `axios` instance (`this.microservicesV1` or `this.microservicesV2`) for the HTTP request (GET, POST, PUT, DELETE).
    //    - **Apply Generics:** Always specify the response type using generics (e.g., `axiosInstance.get<MyResponseType>(url)`).
    //    - **Pass Parameters:** If there are request parameters or a body, pass them according to the `axios` method signature.
    // 5. **Error Handling:** Remember that API response errors (other than 401 for token refresh) are not handled here. The calling Vue components and UI logic are responsible for managing these errors (e.g., displaying alerts, redirects).
    private accessToken = localStorage.getItem('accessToken') || '';
    private idToken = localStorage.getItem('idToken') || '';
    private refreshToken = localStorage.getItem('refreshToken') || '';
    private userName = localStorage.getItem('userName') || '';
    private expirationDate = localStorage.getItem('tokenExpirationDate') || '';
    private refreshTokenTimeout?: number = undefined
    private listeners: { [K in keyof EventMap]?: Array<EventMap[K]> } = {};

    /**
     * Axios instance for V2 API endpoints.
     * Requests made with this instance will automatically include the `accessToken` in the Authorization header.
     * Use this for API calls that require the V2 authentication mechanism.
     */
    microservicesV2 = axios.create({
        baseURL: process.env.VUE_APP_API_ENDPOINT,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }
    })

    /**
     * Axios instance for V1 API endpoints.
     * Requests made with this instance will automatically include the `idToken` in the Authorization header.
     * Use this for API calls that require the V1 authentication mechanism.
     */
    microservicesV1 = axios.create({
        baseURL: process.env.VUE_APP_API_ENDPOINT,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }
    })

    constructor() {
        /**
         * Set Interceptors for refreshing tokens
         */
        this.microservicesV2.interceptors.response.use(response => response, this.refreshTokenInterceptorV2)
        this.microservicesV1.interceptors.response.use(response => response, this.refreshTokenInterceptorV1)

        if (!this.accessToken || !this.idToken || !this.expirationDate) return;

        const tokenExpirationDate = this.getExpirationDateInterval();
        if (tokenExpirationDate <= 0) {
            this.getNewTokens();
            return;
        }
        this.setAxiosAuthHeader();
        this.refreshTokenTimeout = setTimeout(() => {
            this.getNewTokens();
        }, this.getExpirationDateInterval());
    }

    public on(eventName: 'refreshTokenUpdate', callback: (tokens: AuthResponseDto) => void): void;
    public on(eventName: 'refreshTokenFail', callback: () => void): void;
    public on(eventName: keyof EventMap, callback: ((tokens: AuthResponseDto) => void) | (() => void)): void {
        if (eventName === 'refreshTokenUpdate') {
            const cbs = this.listeners.refreshTokenUpdate || [];
            cbs.push(callback as (tokens: AuthResponseDto) => void);
            this.listeners.refreshTokenUpdate = cbs;
        } else { // it must be refreshTokenFail
            const cbs = this.listeners.refreshTokenFail || [];
            cbs.push(callback as () => void);
            this.listeners.refreshTokenFail = cbs;
        }
    }

    private emit<K extends keyof EventMap>(eventName: K, ...args: Parameters<EventMap[K]>): void {
        if (eventName === 'refreshTokenUpdate' && this.listeners.refreshTokenUpdate) {
            const [tokens] = args;
            this.listeners.refreshTokenUpdate.forEach(cb => cb(tokens));
        } else if (eventName === 'refreshTokenFail' && this.listeners.refreshTokenFail) {
            this.listeners.refreshTokenFail.forEach(cb => cb());
        }
    }

    login = async ({ username, password }: { username: string; password: string }) => {
        const url = '/v2/auth/login';

        try {
            const request = await this.microservicesV2.post<AuthResponseDto>(url, { username, password });
            this.accessToken = request.data.accessToken;
            this.refreshToken = request.data.refreshToken;
            this.idToken = request.data.idToken;
            this.userName = username;
            this.setLocalStorageData(request.data, username, request.data.refreshToken);
            this.setAxiosAuthHeader();
            this.calculateExpirationDate(request.data.expiresIn - 10);

            if (this.refreshTokenTimeout) {
                clearTimeout(this.refreshTokenTimeout);
            }

            /**
             * Set timeout for refreshing token so it never expires
             */
            this.refreshTokenTimeout = setTimeout(() => {
                this.getNewTokens();
            }, this.getExpirationDateInterval());
            return request.data;
        } catch (e) {
            this.clearLocalStorageData();
            throw e;
        }
    }

    logout() {
        this.clearLocalStorageData();
        this.accessToken = ''
        this.idToken = ''
        this.refreshToken = ''
        this.userName = ''
        this.expirationDate = ''
        if (this.refreshTokenTimeout) clearTimeout(this.refreshTokenTimeout);
        this.refreshTokenTimeout = undefined
    }

    private setLocalStorageData = (data: AuthResponseDto, username?: string, refreshToken?: string) => {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('idToken', data.idToken);
        username && localStorage.setItem('userName', username);
        refreshToken && localStorage.setItem('refreshToken', refreshToken);
    }

    private clearLocalStorageData = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('userName');
        localStorage.removeItem('tokenExpirationDate');
    }

    private setAxiosAuthHeader = () => {
        if (!this.accessToken || !this.idToken) {
            throw new Error('No access token found in local storage');
        }
        this.microservicesV2.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`;
        this.microservicesV1.defaults.headers.common['Authorization'] = `Bearer ${this.idToken}`;
    }

    private getNewTokens = async () => {
        const url = '/v2/auth/refreshToken';
        if (!this.userName || !this.refreshToken) {
            this.emit('refreshTokenFail');
            throw new Error('No username or refresh token available');
        }
        try {
            const request = await this.microservicesV2.post<AuthResponseDto>(url, { username: this.userName }, {
                headers: {
                    Authorization: `Bearer ${this.refreshToken}`
                }
            });
            this.accessToken = request.data.accessToken;
            this.idToken = request.data.idToken;
            this.setLocalStorageData(request.data);
            this.setAxiosAuthHeader();
            this.calculateExpirationDate(request.data.expiresIn - 10);
            if (this.refreshTokenTimeout) {
                clearTimeout(this.refreshTokenTimeout);
                this.refreshTokenTimeout = setTimeout(() => {
                    this.getNewTokens();
                }, this.getExpirationDateInterval());
            }
            this.emit('refreshTokenUpdate', request.data);
        } catch (error) {
            this.emit('refreshTokenFail');
            this.logout();
            throw error;
        }
    }

    public forceUpdateTokens = (payload: AuthResponseDto) => {
        this.accessToken = payload.accessToken;
        this.idToken = payload.idToken;
        this.setLocalStorageData(payload);
        this.setAxiosAuthHeader();
        this.calculateExpirationDate(payload.expiresIn - 10);
        if (this.refreshTokenTimeout) {
            clearTimeout(this.refreshTokenTimeout);
            this.refreshTokenTimeout = setTimeout(() => {
                this.getNewTokens();
            }, this.getExpirationDateInterval());
        }
    }

    private getExpirationDateInterval = () => {
        if (!this.expirationDate) return 0;
        const expirationDate = new Date(this.expirationDate);
        const now = new Date();
        const interval = expirationDate.getTime() - now.getTime();
        if (interval < 0) return 0;
        return interval;
    }

    private calculateExpirationDate = (expiresIn: number) => {
        const now = new Date();
        now.setSeconds(now.getSeconds() + expiresIn);
        this.expirationDate = now.toISOString();
        localStorage.setItem('tokenExpirationDate', this.expirationDate);
    }

    private refreshTokenInterceptorV2 = async (error: AxiosError) => {
        if (error.response?.status !== 401) {
            throw error;
        }
        await this.getNewTokens();
        error.config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        return this.microservicesV2(error.config);
    }

    private refreshTokenInterceptorV1 = async (error: AxiosError) => {
        if (error.response?.status !== 401) {
            throw error;
        }
        await this.getNewTokens();
        error.config.headers['Authorization'] = `Bearer ${this.idToken}`;
        return this.microservicesV1(error.config);
    }

    /**
     * Fetches clubs associated with the current user.
     * This method uses `microservicesV1` for the API call, implying it uses the `idToken` for authorization.
     * Expected response: An array of club objects (e.g., `PadelClub[]`).
     */
    getMyClubs() {
        const url = '/my-clubs';
        return this.microservicesV1.get<PadelClub[]>(url);
    }

    getPlaygroundsByClubId(clubId: string) {
        const path = `/clubs/${clubId}/playgrounds`;
        return this.microservicesV1.get(path);
    }

    banUser({ userId, clubId }: { userId: string; clubId: string }) {
        const path = `/clubs/${clubId}/users/${userId}/ban`;
        return this.microservicesV1.post(path);
    }

    unbanUser({ userId, clubId }: { userId: string; clubId: string }) {
        const path = `/clubs/${clubId}/users/${userId}/unban`;
        return this.microservicesV1.post(path);
    }

    /**
     * Fetches user information.
     * This method uses `microservicesV2` for the API call, implying it uses the `accessToken` for authorization.
     * Expected response: A user object, type-hinted as `User` from generated sources.
     */
    getUserInfo() {
        const url = '/v2/anagrafica/userinfo';
        return this.microservicesV2.get<{ user: User }>(url);
    }

    getUser({ userId }: { userId: string }) {
        const url = `/v2/anagrafica/user/${userId}`;
        return this.microservicesV2.get<{ user: User }>(url);
    }

    getUsersByClubId(payload: {
        clubId: string;
        page?: number;
        limit?: number;
        filters?: {
            banned?: boolean;
            min_bookings?: number;
            min_cancellations?: number;
            name?: string;
        };
    }) {
        const url = `/v2/gestioneclub/users/${payload.clubId}`;
        return this.microservicesV2.get<UserByClub>(url, {
            params: {
                page: payload.page,
                limit: payload.limit,
                ...payload.filters
            }
        });
    }

    getCalendarEvents(params: {
        club_id: string;
        date?: string;
        start_date?: string;
        user_id?: string;
        future?: boolean;
        archived?: boolean;
        deleted?: boolean;
        hideLoader?: boolean;
    }) {
        const url = '/timeslots/get-calendar'
        return this.microservicesV1.get(url, { params });
    }

    getPaymentsWithFilters(
        clubId: string,
        filters: PaymentFilters
    ) {
        const url = `/v2/gestioneclub/${clubId}/payments`;
        return this.microservicesV2.get<PaymentsResponse>(
            url,
            { params: filters }
        );
    }

    getMemberships({
        clubId,
        userId,
        status,
        page,
        limit,
        sortBy,
        sortDirection,
        filters,
    }: {
        clubId: string;
        userId?: string;
        status?: string;
        page: number;
        limit: number;
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
        filters?: MembershipFilters;
    }) {
        const url = `/v2/membership`;
        return this.microservicesV2.get<MembershipListResponse>(url, {
            params: {
                clubId,
                userId,
                status,
                page,
                limit,
                sortBy,
                sortDirection,
                ...filters,
            }
        })
    }

    updateMembership({ membershipId, membership }: { membershipId: string; membership: UpdateMembershipDto }) {
        const url = `/v2/membership/${membershipId}`;
        return this.microservicesV2.patch<Membership>(url, {
            ...membership,
        })
    }

    getManagers({ clubId, page, limit, orderBy, order }: { clubId: string; page: number; limit: number; orderBy?: string; order?: 'ASC' | 'DESC' }) {
        const url = `/clubs/${clubId}/managers`;
        return this.microservicesV1.get<{ values: ManagerUser[]; total: number }>(url, {
            params: {
                page,
                page_size: limit,
                orderBy,
                order
            }
        });
    }

    addManager({ email, firstName, lastName, phone, clubId, birthDate }: { email: string; firstName: string; lastName: string; phone?: string; clubId: string; birthDate?: string }) {
        const url = `/clubs/${clubId}/managers`;
        return this.microservicesV1.post(url, { email, first_name: firstName, last_name: lastName, phone_number: phone, date_of_birth: birthDate });
    }

    confirmManagerRegistration({ token, password, confirmPassword, privacyAccepted }: { token: string; password: string; confirmPassword: string; privacyAccepted: boolean }) {
        const url = `/clubs/auth/complete-registration`;
        return this.microservicesV1.post(url, {
            password,
            confirm_password: confirmPassword,
            privacy_accepted: privacyAccepted
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    resendManagerInvitation({ clubId, userId }: { clubId: string; userId: string }) {
        const url = `/clubs/${clubId}/managers/${userId}/resend-invitation`;
        return this.microservicesV1.post(url);
    }

    disableManager({ clubId, userId }: { clubId: string; userId: string }) {
        const url = `/clubs/${clubId}/managers/${userId}/disable`;
        return this.microservicesV1.patch(url);
    }

    userIsClubOwner({ clubId }: { clubId: string }) {
        const url = `/v2/gestioneclub/${clubId}/is-owner`;
        return this.microservicesV2.get<{ message: { is_owner: boolean } }>(url);
    }

    enableManager({ clubId, userId }: { clubId: string; userId: string }) {
        const url = `/clubs/${clubId}/managers/${userId}/enable`;
        return this.microservicesV1.patch(url);
    }
}

export const client = new ApiClient();